import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, ArrowRight, Loader2, X, RotateCcw, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import api from '@/lib/api/client'
import type { AiFilterCondition } from '@/types/components/audience-filter-modal'
import { ASK_AI_FILTER_UNAVAILABLE_CODE } from '@digital-twins/shared-types'

interface InlineAskAiFilterProps {
  populationId: string
  currentFilters: Record<string, Array<string>>
  onApplyFilters: (filters: Record<string, Array<string>>, chips: AiFilterCondition[], mode: 'replace' | 'add') => void
  /** Opens the picker with these choices as its own selections, to fine-tune. */
  onOpenFilterModal: (conditions: Array<AiFilterCondition>) => void
  disabled?: boolean
  totalPersonas: number
  audienceType?: string | null
  /**
   * When provided, overrides the backend matchingCount with a scoped preview
   * (e.g., study twins only), built by the picker's one filter builder.
   */
  onScopedPreview?: (conditions: AiFilterCondition[]) => Promise<{ matchingCount: number; totalCount: number }>
}

type FilterState = 'input' | 'loading' | 'results' | 'rejected' | 'error'

export function InlineAskAiFilter({
  populationId,
  currentFilters,
  onApplyFilters,
  onOpenFilterModal,
  disabled,
  totalPersonas,
  audienceType,
  onScopedPreview,
}: InlineAskAiFilterProps) {
  const { t } = useTranslation('app')
  const [query, setQuery] = useState('')
  const [state, setState] = useState<FilterState>('input')
  const [conditions, setConditions] = useState<AiFilterCondition[]>([])
  const [matchingCount, setMatchingCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [rejectionReason, setRejectionReason] = useState<string | null>(null)
  const [submitDisabledUntil, setSubmitDisabledUntil] = useState(0)
  const [conditionsModified, setConditionsModified] = useState(false)
  const [droppedCount, setDroppedCount] = useState(0)
  const [errorType, setErrorType] = useState<'network' | 'unavailable' | 'server'>('server')

  const inputRef = useRef<HTMLInputElement>(null)
  const skipRecomputeRef = useRef(false)

  useEffect(() => {
    if (state === 'input') {
      inputRef.current?.focus()
    }
  }, [state])

  const reset = useCallback(() => {
    setState('input')
    setConditions([])
    setMatchingCount(0)
    setRejectionReason(null)
    setConditionsModified(false)
    setDroppedCount(0)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (query.trim().length < 3 || Date.now() < submitDisabledUntil) return

    setState('loading')
    setSubmitDisabledUntil(Date.now() + 2000)

    try {
      const { data } = await api.post(`/populations/${populationId}/ask-ai-filter`, {
        query: query.trim(),
      })

      if (data.rejected) {
        setState('rejected')
        setRejectionReason(data.rejectionReason || 'No match in the data')
        return
      }

      // Nest is the one boundary for the AI's choices (C09-1): it checked each
      // against the catalog and counted the filter the picker builds for them.
      const validConditions = data.conditions as Array<AiFilterCondition>
      if (validConditions.length === 0) {
        setState('rejected')
        setRejectionReason('No valid filters could be generated — try rephrasing')
        return
      }

      setConditions(validConditions)
      setDroppedCount(data.droppedConditions?.length ?? 0)
      skipRecomputeRef.current = true

      if (onScopedPreview) {
        try {
          const result = await onScopedPreview(validConditions)
          setMatchingCount(result.matchingCount)
          setTotalCount(result.totalCount)
        } catch {
          setConditionsModified(true)
        }
      } else {
        setMatchingCount(data.matchingCount)
        setTotalCount(data.totalCount)
      }

      setConditionsModified(false)
      setState('results')
    } catch (err: unknown) {
      const isNetwork = err instanceof TypeError || (err as { code?: string })?.code === 'ERR_NETWORK'
      // The AI call failed or ran past its deadline: Nest's typed code (C09-5).
      const isUnavailable =
        (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code ===
        ASK_AI_FILTER_UNAVAILABLE_CODE
      setErrorType(isNetwork ? 'network' : isUnavailable ? 'unavailable' : 'server')
      setState('error')
    }
  }, [query, populationId, submitDisabledUntil, onScopedPreview])

  const removeCondition = useCallback((index: number) => {
    setConditions((prev) => {
      const next = prev.filter((_, i) => i !== index)
      if (next.length === 0) {
        setState('input')
        setConditionsModified(false)
        return []
      }
      return next
    })
  }, [])

  // Recompute match count whenever conditions change (e.g. after removal)
  useEffect(() => {
    if (conditions.length === 0 || state !== 'results') return
    if (skipRecomputeRef.current) {
      skipRecomputeRef.current = false
      return
    }
    let cancelled = false

    async function recompute() {
      // Without a preview the count is unknown until the filter is applied:
      // there is no second evaluator here (C09-1).
      if (!onScopedPreview) {
        if (!cancelled) setConditionsModified(true)
        return
      }
      try {
        const result = await onScopedPreview(conditions)
        if (!cancelled) {
          setMatchingCount(result.matchingCount)
          setTotalCount(result.totalCount)
          setConditionsModified(false)
        }
      } catch {
        if (!cancelled) setConditionsModified(true)
      }
    }

    recompute()
    return () => { cancelled = true }
  }, [conditions, state, onScopedPreview])

  const handleApply = useCallback(
    (mode: 'replace' | 'add') => {
      const newFilters: Record<string, Array<string>> = mode === 'add' ? { ...currentFilters } : {}
      for (const cond of conditions) {
        const existing = newFilters[cond.column] || []
        const merged = [...new Set([...existing, ...cond.values])]
        newFilters[cond.column] = merged
      }
      onApplyFilters(newFilters, conditions, mode)
      reset()
    },
    [conditions, currentFilters, onApplyFilters, reset],
  )

  const handleFineTune = useCallback(() => {
    onOpenFilterModal(conditions)
    reset()
  }, [conditions, onOpenFilterModal, reset])

  if (totalPersonas < 10) return null

  const placeholder = audienceType?.toUpperCase() === 'B2B'
    ? t('creation.askAi.placeholderB2b')
    : t('creation.askAi.placeholderB2c')

  const canSubmit = query.trim().length >= 3 && Date.now() >= submitDisabledUntil && !disabled
  const isResultsDisabled = !conditionsModified && (matchingCount === 0 || matchingCount >= totalCount)

  return (
    <div
      data-testid="ask-ai-filter-section"
      className="rounded-xl bg-[#f1f1f1]/50 dark:bg-muted/40"
    >
      <div className="px-3 py-3">
        {state === 'input' && (
          <>
            <div className="flex items-start gap-3 mb-2.5">
              <div className="h-7 w-8 flex items-center justify-center text-muted-foreground shrink-0">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-normal text-[20px] text-foreground font-display">{t('creation.askAi.describe')}</span>
                <p className="text-[14px] text-muted-foreground mt-0.5">{t('creation.askAi.hint')}</p>
              </div>
            </div>
            <div className="relative">
              <input
                ref={inputRef}
                placeholder={placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && canSubmit && handleSubmit()}
                disabled={disabled}
                data-testid="ask-ai-input"
                className={cn(
                  'w-full h-10 pl-3 pr-11 rounded-lg text-sm',
                  'bg-transparent',
                  'border border-border/40 dark:border-white/10',
                  'placeholder:text-muted-foreground/60 dark:placeholder:text-white/30',
                  'focus:outline-none focus:border-input-focus/40 dark:focus:border-input-focus/30',
                  'transition-colors duration-150',
                )}
              />
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                data-testid="ask-ai-submit"
                className={cn(
                  'absolute right-1.5 top-1/2 -translate-y-1/2',
                  'h-7 w-7 rounded-md flex items-center justify-center',
                  'transition-all duration-150',
                  canSubmit
                    ? 'bg-cta text-cta-foreground hover:opacity-90'
                    : 'text-cta',
                )}
              >
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </>
        )}

        {state === 'loading' && (
          <div className="flex items-center gap-2.5 py-2">
            <Loader2 className="h-4 w-4 animate-spin text-input-focus" />
            <span className="text-sm text-muted-foreground">{t('creation.askAi.finding', { query })}</span>
          </div>
        )}

        {state === 'results' && (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-normal text-[20px] text-foreground font-display">{t('creation.askAi.suggested')}</span>
                  {!conditionsModified && matchingCount > 0 && (
                    <span className={cn(
                      'text-[14px] font-medium',
                      matchingCount >= totalCount
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-muted-foreground',
                    )}>
                      ({matchingCount.toLocaleString()} {matchingCount >= totalCount ? 'all' : 'matches'})
                    </span>
                  )}
                  {!conditionsModified && matchingCount === 0 && (
                    <span className="text-[14px] font-medium text-destructive">(no matches)</span>
                  )}
                </div>
                <p className="text-[13px] text-muted-foreground mt-0.5 truncate">
                  for &ldquo;{query}&rdquo;
                </p>
              </div>
              <button
                onClick={reset}
                data-testid="ask-ai-try-again"
                className="mt-1 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {conditions.map((cond, i) => (
                  <div
                  key={cond.column}
                  className="group/chip relative flex items-start gap-1.5 rounded-lg border border-border/60 dark:border-white/10 bg-white dark:bg-[hsl(var(--card-inset))] px-2.5 py-1.5 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-foreground">{cond.chip}</span>
                    {cond.displayQuestion && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[200px]">
                        {cond.displayQuestion}{cond.displayAnswer ? `: ${cond.displayAnswer}` : ''}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => removeCondition(i)}
                    className="mt-0.5 shrink-0 rounded p-0.5 hover:bg-foreground/10 dark:hover:bg-white/10 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  {cond.displayQuestion && (
                    <div className="pointer-events-none absolute bottom-full left-0 mb-1.5 px-2 py-1 rounded bg-popover border border-border shadow-md text-xs text-popover-foreground max-w-[260px] whitespace-normal opacity-0 group-hover/chip:opacity-100 transition-opacity z-[60]">
                      {cond.displayQuestion}{cond.displayAnswer ? `: ${cond.displayAnswer}` : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {droppedCount > 0 && !conditionsModified && (
              <div className="flex items-center gap-1.5 rounded-md bg-amber-50 dark:bg-amber-900/20 px-2 py-1.5" data-testid="ask-ai-partial-match">
                <AlertTriangle className="h-3 w-3 shrink-0 text-amber-600" />
                <span className="text-xs text-amber-700 dark:text-amber-400">
                  {t('creation.askAi.dropped', { count: droppedCount })}
                </span>
              </div>
            )}

            {!conditionsModified && matchingCount === 0 && (
              <p className="text-[14px] text-destructive">{t('creation.askAi.noMatch')}</p>
            )}
            {!conditionsModified && matchingCount > 0 && matchingCount >= totalCount && (
              <p className="text-[14px] text-amber-600 dark:text-amber-400">{t('creation.askAi.entireAudience')}</p>
            )}

            <div className="flex items-center justify-center gap-2">
              <Button
                variant="secondary"
                className="h-9 px-4 text-[14px] rounded-lg"
                onClick={handleFineTune}
                data-testid="ask-ai-fine-tune"
              >
                {t('creation.askAi.adjust')}
              </Button>
              <Button
                variant="cta"
                className="h-9 px-4 text-[14px] rounded-lg"
                disabled={isResultsDisabled}
                onClick={() => handleApply('replace')}
                data-testid="ask-ai-apply"
              >
                {t('creation.askAi.apply')}
              </Button>
            </div>
          </div>
        )}

        {state === 'rejected' && (
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-muted-foreground">{rejectionReason}</span>
            <button
              onClick={reset}
              data-testid="ask-ai-retry"
              className="text-xs text-input-focus hover:opacity-80 transition-opacity flex items-center gap-1"
            >
              <RotateCcw className="h-3 w-3" />
              {t('creation.askAi.retry')}
            </button>
          </div>
        )}

        {state === 'error' && (
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-destructive">
              {errorType === 'network'
                ? t('creation.askAi.errorNetwork')
                : errorType === 'unavailable'
                  ? t('creation.askAi.errorUnavailable')
                  : t('creation.askAi.errorGeneric')}
            </span>
            <button
              onClick={reset}
              data-testid="ask-ai-retry-error"
              className="text-xs text-input-focus hover:opacity-80 transition-opacity flex items-center gap-1"
            >
              <RotateCcw className="h-3 w-3" />
              {t('creation.askAi.retry')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
